import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { GreshamHeader } from "../components/GreshamHeader";
import { GreshamFooter } from "../components/GreshamFooter";

const Advisory: React.FC = () => {
  const textColor = useColorModeValue("gray.800", "white");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.pageYOffset;
        const rate = scrolled * -0.5;
        parallaxRef.current.style.transform = `translateY(${rate}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [formData, setFormData] = useState({
    companyName: "",
    acn: "",
    email: "",
    industry: "",
    numberOfEmployee: "",
    website: "",
    message: ""
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Numeric-only fields
    if (name === "acn" || name === "numberOfEmployee") {
      const cleaned = value.replace(/[^0-9]/g, "");
      setFormData(prev => ({
        ...prev,
        [name]: cleaned,
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Optional website: if provided, it must look like a domain name
    if (formData.website.trim()) {
      const domainRegex = /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
      if (!domainRegex.test(formData.website.trim())) {
        toast({
          title: "Invalid website",
          description: "Please enter a website like example.com (without http://).",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    const apiUrl = process.env.REACT_APP_FORM_API_URL;

    if (!apiUrl) {
      toast({
        title: "Form API not configured",
        description: "Please configure REACT_APP_FORM_API_URL.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          formType: "advisory",
          ...formData,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Advisory form submission error", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });

        let errorMessage = "Something went wrong while sending your enquiry. Please try again.";
        try {
          const parsed = JSON.parse(errorText);
          if (parsed && typeof parsed.message === "string") {
            errorMessage = parsed.message;
          }
        } catch {
          // ignore JSON parse errors
        }

        throw new Error(errorMessage);
      }

      setFormData({
        companyName: "",
        acn: "",
        email: "",
        industry: "",
        numberOfEmployee: "",
        website: "",
        message: "",
      });

      onOpen();
    } catch (error: any) {
      console.error("Advisory form submission failed", error);
      toast({
        title: "Submission failed",
        description: error?.message || "Something went wrong while sending your enquiry. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      bg="white"
      css={{
        overscrollBehavior: "none",
        "-webkit-overflow-scrolling": "touch"
      }}
    >
      <GreshamHeader />

      {/* Hero Section with Background Image */}
      <Box
        as="section"
        w="full"
        position="relative"
        h="100vh"
        minH="600px"
        overflow="hidden"
        clipPath="inset(0)"
      >
        {/* Background Image Container with Parallax */}
        <Box
          ref={parallaxRef}
          position="fixed"
          top="0"
          left="0"
          right="0"
          height="120vh"
          backgroundImage="url('/background/zhu-yunxiao-pGyL9zDMap4-unsplash.jpg')"
          backgroundSize="cover"
          backgroundPosition="center 40%"
          backgroundRepeat="no-repeat"
          filter="brightness(0.7)"
          willChange="transform"
        />

        {/* Text Container - Overlay on background for mobile, positioned for desktop */}
        <Box
          position={{ base: "absolute", lg: "absolute" }}
          top={{ base: "50%", lg: 0 }}
          left={{ base: 0, lg: 0 }}
          w={{ base: "full", lg: "70%" }}
          h={{ base: "auto", lg: "calc(120px + 300px)", xl: "calc(140px + 300px)", "2xl": "calc(160px + 300px)" }}
          display="flex"
          alignItems={{ base: "center", lg: "flex-end" }}
          justifyContent={{ base: "center", lg: "flex-start" }}
          zIndex={100}
          bg={{ base: "transparent", lg: "transparent" }}
          mb={{ base: 0, lg: 0 }}
          order={{ base: 1, lg: 0 }}
          pt={{ base: 0, md: 0, lg: "120px", xl: "140px", "2xl": "160px" }}
          pb={{ base: 0, md: 0, lg: 0 }}
          px={{ base: 6, sm: 8, md: 12, lg: 16 }}
          transform={{ base: "translateY(-50%)", lg: "none" }}
        >
          <VStack spacing={3} align="flex-start" maxW={{ base: "90%", sm: "80%", md: "700px", lg: "800px" }} ml={{ base: "auto", lg: "10%" }} mr="auto" textAlign="left" justify="center">
            {/* Space placeholder like homepage */}
            <Text
              fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
              color="white"
              fontFamily="'Carmen Sans Regular', sans-serif"
              fontWeight="normal"
              letterSpacing="0.05em"
              className="slide-in-left"
            >
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </Text>

            {/* Main Heading */}
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
              fontWeight="bold"
              color="white"
              fontFamily="'Carmen Sans Bold', sans-serif"
              lineHeight="1.15"
              maxW={{ base: "100%", md: "900px", lg: "1000px" }}
              className="slide-in-left-delay-1"
            >
              Get expert guidance
            </Heading>
          </VStack>
        </Box>
      </Box>

      {/* History Section with Image */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        bg="white"
        mt="-80px"
        position="relative"
        zIndex={20}
        borderRadius="3xl 3xl 0 0"
        boxShadow="0 -20px 40px rgba(0,0,0,0.05)"
      >
        <Box
          px={{ base: 6, md: 12, lg: 16 }}
          w="full"
        >
          {/* Two Column Content Layout with Image */}
          <Box display="flex" flexDirection={{ base: "column", lg: "row" }} gap={{ base: 8, lg: 12 }}>
            {/* Left Column - Dynamic Image (Smaller) */}
            <Box flex={{ base: "1", lg: "0.4" }}>
              <Box
                w="full"
                h={{ base: "280px", md: "320px", lg: "380px" }}
                borderRadius="lg"
                overflow="hidden"
                backgroundImage="url('/background/laura-adai-DNZjccZ3Drg-unsplash.jpg')"
                backgroundSize="cover"
                backgroundPosition="center 80%"
                backgroundRepeat="no-repeat"
                position="relative"
                aspectRatio={{ base: "4/3", md: "3/2" }}
              >
                {/* Subtle overlay for better visual integration */}
                <Box
                  position="absolute"
                  inset={0}
                  bg="linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(244, 229, 194, 0.05) 50%, rgba(212, 175, 55, 0.1) 100%)"
                />
              </Box>
            </Box>

            {/* Right Column - Text Content (Larger) */}
            <Box flex={{ base: "1", lg: "0.6" }} display="flex" alignItems="center" h={{ base: "auto", lg: "400px" }}>
              <Box>
                <VStack spacing={6} align="flex-start">
                  {/* Main Heading */}
                  <Heading
                    as="h2"
                    fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                    fontWeight="bold"
                    color={textColor}
                    fontFamily="'Carmen Sans Bold', sans-serif"
                    lineHeight="1.2"
                    letterSpacing="0.01em"
                    mb={8}
                  >
                    Leveraging Mercurian Equity's expertise and network to help strengthen and grow companies.
                  </Heading>

                  {/* Description Paragraph */}
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.7"
                    fontWeight="300"
                    opacity={0.9}
                  >
                    At Mercurian Equity, we specialise in delivering strategic corporate advisory services to mid-market and emerging growth companies, helping them navigate complex financial landscapes and achieve their business objectives.
                  </Text>
                </VStack>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Advisory Services Section - Three Modules */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        bg="white"
      >
        <Box
          px={{ base: 6, md: 12, lg: 16 }}
          w="full"
        >
          {/* Three Column Modules */}
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing={{ base: 8, md: 12, lg: 16 }}
            mb={0}
          >
            {/* Commercial Excellence Module */}
            <Box
              bg="#ffffff"
              py={{ base: 6, md: 8 }}
              px={{ base: 4, md: 6 }}
              textAlign="center"
            >
              <Box mb={6} minH="90px" display="flex" alignItems="flex-end">
                <Heading
                  as="h3"
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight="bold"
                  color={textColor}
                  fontFamily="'Carmen Sans Medium', sans-serif"
                  letterSpacing="0.05em"
                  pb={3}
                  textAlign="center"
                  position="relative"
                  width="100%"
                  _after={{
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '1px',
                    bg: 'gray.400'
                  }}
                >
                 Revenue Growth Advisory
                </Heading>
              </Box>
              <VStack spacing={4} align="center">
                <Text fontSize={{ base: "sm", md: "md" }} color={textColor} fontFamily="'Carmen Sans Light', sans-serif" fontWeight="300" lineHeight="1.7" textAlign="center">
                  Supporting our clients to develop commercial excellence capabilities such as sales optimisation, marketing effectiveness, customer segmentation, pricing strategies, and go-to-market model refinement to ignite profitable and organic revenue growth.
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }} color={textColor} fontFamily="'Carmen Sans Light', sans-serif" fontWeight="300" lineHeight="1.7" textAlign="center">
                  Mercurian's approach centres on creating margin transparency and implementing improvement levers e.g. revenue management through sales force and channel optimisation, margin management, product portfolio optimisation and pricing strategies.
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }} color={textColor} fontFamily="'Carmen Sans Light', sans-serif" fontWeight="300" lineHeight="1.7" textAlign="center">
                  Our commercial excellence advisory provides strategic support throughout the investment lifecycle, from commercial due diligence pre-acquisition through to dynamic portfolio management during the hold period.
                </Text>
              </VStack>
            </Box>

            {/* Digital Transformation Module */}
            <Box
              bg="#f7f7f7"
              py={{ base: 6, md: 8 }}
              px={{ base: 4, md: 6 }}
              textAlign="center"
            >
              <Box mb={6} minH="90px" display="flex" alignItems="flex-end">
                <Heading
                  as="h3"
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight="bold"
                  color={textColor}
                  fontFamily="'Carmen Sans Medium', sans-serif"
                  letterSpacing="0.05em"
                  pb={3}
                  textAlign="center"
                  position="relative"
                  width="100%"
                  _after={{
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '1px',
                    bg: 'gray.400'
                  }}
                >
                  Technology Enablement Advisory
                </Heading>
              </Box>
              <VStack spacing={4} align="center">
                <Text fontSize={{ base: "sm", md: "md" }} color={textColor} fontFamily="'Carmen Sans Light', sans-serif" fontWeight="300" lineHeight="1.7" textAlign="center">
                  Digitally transforming portfolio companies' products, operations, and business models through strategic deployment of cloud platforms, artificial intelligence, data analytics, and IoT technologies.
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }} color={textColor} fontFamily="'Carmen Sans Light', sans-serif" fontWeight="300" lineHeight="1.7" textAlign="center">
                  Our service includes assessing digital maturity, developing transformation roadmaps, implementing cloud migrations, deploying AI and automation tools, and building data-driven decision-making capabilities.
                </Text>
              </VStack>
            </Box>

            {/* Human Capital Module */}
            <Box
              bg="#ffffff"
              py={{ base: 6, md: 8 }}
              px={{ base: 4, md: 6 }}
              textAlign="center"
            >
              <Box mb={6} minH="90px" display="flex" alignItems="flex-end">
                <Heading
                  as="h3"
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight="bold"
                  color={textColor}
                  fontFamily="'Carmen Sans Medium', sans-serif"
                  letterSpacing="0.05em"
                  pb={3}
                  textAlign="center"
                  position="relative"
                  width="100%"
                  _after={{
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '1px',
                    bg: 'gray.400'
                  }}
                >
                  Human Capital & Talent Advisory

                </Heading>
              </Box>
              <VStack spacing={4} align="center">
                <Text fontSize={{ base: "sm", md: "md" }} color={textColor} fontFamily="'Carmen Sans Light', sans-serif" fontWeight="300" lineHeight="1.7" textAlign="center">
                  Our comprehensive service goes beyond executive search to provide strategic human capital advisory throughout the organisation's lifecycle, including pre-acquisition talent assessment, HR readiness evaluation, workforce management strategy, culture alignment, retention planning, and leadership development.
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }} color={textColor} fontFamily="'Carmen Sans Light', sans-serif" fontWeight="300" lineHeight="1.7" textAlign="center">
                  This service includes human capital due diligence during transactions, organisational alignment in the post-close period, and ongoing talent strategy to support value creation and exit readiness.
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Box>
      </Box>

      {/* Contact Form Section */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
      >
        <Box
          px={{ base: 6, md: 12, lg: 16 }}
          w="full"
        >
          <Box
            display="flex"
            flexDirection={{ base: "column", lg: "row" }}
            gap={{ base: 8, lg: 12 }}
            w="full"
          >
            {/* Contact Form Container */}
            <Box
              flex="1"
              bg="white"
              p={{ base: 6, md: 8 }}
              borderRadius="md"
            >
              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <VStack spacing={6} align="stretch">

                  {/* Company Name and ACN Row */}
                  <Box display="flex" flexDirection={{ base: "column", lg: "row" }} gap={6}>
                    <Box flex="1">
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        mb={2}
                        textTransform="uppercase"
                        letterSpacing="1px"
                        fontFamily="'Carmen Sans Light', sans-serif"
                      >
                        COMPANY NAME
                      </Text>
                      <Box
                        borderBottom="1px solid"
                        borderColor="gray.300"
                        pb={2}
                      >
                        <input
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          required
                          style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            fontSize: "16px",
                            fontFamily: "'Carmen Sans Light', sans-serif",
                          }}
                        />
                      </Box>
                    </Box>
                    <Box flex="1">
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        mb={2}
                        textTransform="uppercase"
                        letterSpacing="1px"
                        fontFamily="'Carmen Sans Light', sans-serif"
                      >
                        ACN
                      </Text>
                      <Box
                        borderBottom="1px solid"
                        borderColor="gray.300"
                        pb={2}
                      >
                        <input
                          name="acn"
                          value={formData.acn}
                          onChange={handleInputChange}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            fontSize: "16px",
                            fontFamily: "'Carmen Sans Light', sans-serif",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Email and Industry Row */}
                  <Box display="flex" flexDirection={{ base: "column", lg: "row" }} gap={6}>
                    <Box flex="1">
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        mb={2}
                        textTransform="uppercase"
                        letterSpacing="1px"
                        fontFamily="'Carmen Sans Light', sans-serif"
                      >
                        EMAIL
                      </Text>
                      <Box
                        borderBottom="1px solid"
                        borderColor="gray.300"
                        pb={2}
                      >
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            fontSize: "16px",
                            fontFamily: "'Carmen Sans Light', sans-serif",
                          }}
                        />
                      </Box>
                    </Box>
                    <Box flex="1">
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        mb={2}
                        textTransform="uppercase"
                        letterSpacing="1px"
                        fontFamily="'Carmen Sans Light', sans-serif"
                      >
                        INDUSTRY
                      </Text>
                      <Box
                        borderBottom="1px solid"
                        borderColor="gray.300"
                        pb={2}
                      >
                        <input
                          name="industry"
                          value={formData.industry}
                          onChange={handleInputChange}
                          style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            fontSize: "16px",
                            fontFamily: "'Carmen Sans Light', sans-serif",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Number of Employee */}
                  <Box>
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="1px"
                      fontFamily="'Carmen Sans Light', sans-serif"
                    >
                      NUMBER OF EMPLOYEE
                    </Text>
                    <Box
                      borderBottom="1px solid"
                      borderColor="gray.300"
                      pb={2}
                    >
                      <input
                        name="numberOfEmployee"
                        value={formData.numberOfEmployee}
                        onChange={handleInputChange}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "16px",
                          fontFamily: "'Carmen Sans Light', sans-serif",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Website */}
                  <Box>
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="1px"
                      fontFamily="'Carmen Sans Light', sans-serif"
                    >
                      WEBSITE
                    </Text>
                    <Box
                      borderBottom="1px solid"
                      borderColor="gray.300"
                      pb={2}
                    >
                      <input
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        placeholder="example.com"
                        pattern="^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$"
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "16px",
                          fontFamily: "'Carmen Sans Light', sans-serif",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Your Message */}
                  <Box>
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="1px"
                      fontFamily="'Carmen Sans Light', sans-serif"
                    >
                      YOUR MESSAGE
                    </Text>
                    <Box
                      borderBottom="1px solid"
                      borderColor="gray.300"
                      pb={2}
                    >
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "16px",
                          fontFamily: "'Carmen Sans Light', sans-serif",
                          resize: "vertical",
                          minHeight: "100px",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Submit Button */}
                  <Box>
                    <Box
                      as="button"
                      type="submit"
                      bg="black"
                      color="white"
                      w="full"
                      py={3}
                      borderRadius="md"
                      fontSize="sm"
                      fontWeight="bold"
                      textTransform="uppercase"
                      cursor="pointer"
                      transition="all 0.3s ease"
                      _hover={{ bg: "gray.800" }}
                      disabled={isSubmitting}
                      opacity={isSubmitting ? 0.6 : 1}
                    >
                      {isSubmitting ? "Submitting..." : "Send Enquiry"}
                    </Box>
                  </Box>
                </VStack>
              </form>
            </Box>

            {/* Contact Information Container */}
            <Box
              flex={{ base: "1", lg: "0.5" }}
              alignSelf="center"
              pl={{ base: 0, lg: 8 }}
            >
              <VStack spacing={4} align="stretch" justify="center" h="full">
                {/* Contact Us Section */}
                <Box>
                  <Heading
                    as="h3"
                    fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                    fontWeight="bold"
                    color={textColor}
                    mb={6}
                    lineHeight="1.2"
                    fontFamily="'Carmen Sans Bold', sans-serif"
                  >
                    Mercurian Market Leader Program
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    lineHeight="1.6"
                    maxW={{ base: "100%", lg: "400px" }}
                    mb={4}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                    opacity={0.8}
                  >
                    Mercurian works with businesses operating in the lower and middle markets to develop and implement various growth strategies and operational improvement initiatives at no upfront costs.
                    <br /><br />
                    Businesses that qualify for our Market Leader Program benefit from hands on Advisory Services and operational-level involvement from our team of expert advisors to drive scale and growth.
                  </Text>
                </Box>
              </VStack>
            </Box>
          </Box>
        </Box>
      </Box>

      <GreshamFooter />

      {/* Submission Success Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent mx={4}>
          <ModalHeader
            fontSize="2xl"
            fontWeight="bold"
            fontFamily="'Carmen Sans Bold', sans-serif"
            pt={8}
            pb={4}
            textAlign="center"
          >
            Thank you
          </ModalHeader>
          <ModalBody pb={6} px={8}>
            <VStack spacing={4} align="center">
              <Text
                fontSize="lg"
                textAlign="center"
                color="gray.700"
                fontFamily="'Carmen Sans Light', sans-serif"
                lineHeight="1.6"
              >
                Your message has been successfully received. A member of our
                team will be in contact with you shortly.
              </Text>
             
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center" pb={8}>
            <Button
              onClick={onClose}
              bg="black"
              color="white"
              size="lg"
              px={12}
              py={6}
              fontSize="md"
              fontWeight="bold"
              textTransform="uppercase"
              _hover={{ bg: "gray.800" }}
              fontFamily="'Carmen Sans Regular', sans-serif"
            >
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Advisory;